// export default CourseEditor;

import React, { useState, useEffect } from 'react';
import './CourseEditor.css';
import CourseStructure from '../CourseStructure/course/CourseStructure';
import CourseDetailsEditForm from '../../CourseDetailsEditForm/CourseDetailsEditForm';
import { getMyProfile } from '../../../api/ProfileApis';
import { getAllCourses, updateCourse, deleteCourse } from '../../../api/CoursesApi';
import CourseCreator from '../categorymanagement/CourseCreator';
import CategoryManager from '../categorymanagement/CategoryManager';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import Footer from '../../common/Footer';

const CourseEditor = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('course'); // 'course' | 'project'
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    course: null,
  });

  // Load courses from API
  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setIsLoading(true);
      const profileResponse = await getMyProfile();
      const userId = profileResponse.data.id;
      const coursesResponse = await getAllCourses();
      const myCourses = coursesResponse.data.filter(
        (course) => course.instructor === userId
      );
      setCourses(myCourses);
    } catch (error) {
      alert('Error loading courses: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Derived list based on the current toggle
  const visibleCourses = courses.filter((c) =>
    typeFilter === 'project' ? c.is_project === true : !c.is_project
  );

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setActiveTab('structure');
  };

  const handleCourseUpdate = async (updatedCourseData) => {
    if (!selectedCourse) {
      return;
    }

    const isFormData = updatedCourseData instanceof FormData;
    const isResponseData =
      updatedCourseData &&
      typeof updatedCourseData === 'object' &&
      updatedCourseData.id &&
      updatedCourseData.title;

    if (isResponseData && !isFormData) {
      setSelectedCourse(updatedCourseData);
      setCourses((prev) =>
        prev.map((course) =>
          course.id === updatedCourseData.id ? updatedCourseData : course
        )
      );
      return;
    }

    // Update via slug — /api/course/<slug>/
    const slugOrId = selectedCourse.slug || selectedCourse.id;

    setIsLoading(true);
    try {
      const response = await updateCourse(slugOrId, updatedCourseData);

      setSelectedCourse(response.data);
      setCourses((prev) =>
        prev.map((course) =>
          course.id === selectedCourse.id ? response.data : course
        )
      );

      alert('Course updated successfully!');
    } catch (error) {
      let errorMsg = 'Error updating course: ';
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const errors = [];
          for (const key in error.response.data) {
            if (Array.isArray(error.response.data[key])) {
              errors.push(`${key}: ${error.response.data[key].join(', ')}`);
            } else {
              errors.push(`${key}: ${error.response.data[key]}`);
            }
          }
          errorMsg += errors.join('\n');
        } else {
          errorMsg += error.response.data;
        }
      } else {
        errorMsg += error.message || 'Unknown error';
      }

      alert(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseCreated = (newCourse) => {
    const normalized = {
      ...newCourse,
      is_project:
        newCourse.is_project ?? (typeFilter === 'project'),
    };
    setCourses((prev) => [...prev, normalized]);
    setSelectedCourse(normalized);
    setActiveTab('structure');
  };

  const handleDeleteClick = (course) => {
    setDeleteModal({
      isOpen: true,
      course: course,
    });
  };

  const handleCloseModal = () => {
    setDeleteModal({
      isOpen: false,
      course: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.course) return;

    const course = deleteModal.course;
    const courseTitle = course.title;

    // Delete via slug — /api/course/<slug>/
    const slugOrId = course.slug || course.id;

    try {
      setIsLoading(true);
      await deleteCourse(slugOrId);

      setCourses((prev) => prev.filter((c) => c.id !== course.id));

      if (selectedCourse && selectedCourse.id === course.id) {
        setSelectedCourse(null);
        setActiveTab('overview');
      }

      handleCloseModal();

      alert(`Course "${courseTitle}" deleted successfully!`);
    } catch (error) {
      alert(
        'Error deleting course: ' +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleStructureUpdate = (sections) => {
    // intentionally empty
  };

  const handleStructureSave = (saveResult) => {
    // intentionally empty
  };

  // Navigation
  const navigateToOverview = () => {
    setSelectedCourse(null);
    setActiveTab('overview');
  };

  const navigateToCreateCourse = () => {
    setActiveTab('create-course');
  };

  const navigateToManageCategories = () => {
    setActiveTab('manage-categories');
  };

  const switchType = (nextType) => {
    if (nextType === typeFilter) return;
    setTypeFilter(nextType);
    setSelectedCourse(null);
    setActiveTab('overview');
  };

  // Labels based on current type
  const typeLabel = typeFilter === 'project' ? 'Project' : 'Course';
  const typeLabelPlural = typeFilter === 'project' ? 'Projects' : 'Courses';
  const typeLabelLower = typeFilter === 'project' ? 'project' : 'course';

  return (
    <div>
      <div className="course-editor">
        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          itemName={deleteModal.course?.title}
          itemType={typeLabelLower}
          isLoading={isLoading}
        />

        {/* Header */}
        <div className="editor-header">
          <div className="header-main">
            <h1>
              {typeFilter === 'project'
                ? 'Project Management'
                : 'Course Management'}
            </h1>
            <div className="header-subtitle">
              {selectedCourse
                ? `Editing: ${selectedCourse.title}`
                : typeFilter === 'project'
                ? 'Create and manage your projects'
                : 'Create and manage your courses'}
            </div>

            {/* Type toggle */}
            <div className="editor-type-toggle">
              <button
                className={`editor-type-toggle__btn ${
                  typeFilter === 'course' ? 'active' : ''
                }`}
                onClick={() => switchType('course')}
              >
                📚 Courses
              </button>
              <button
                className={`editor-type-toggle__btn ${
                  typeFilter === 'project' ? 'active' : ''
                }`}
                onClick={() => switchType('project')}
              >
                🛠️ Projects
              </button>
            </div>
          </div>

          {/* Main Navigation */}
          <nav className="main-navigation">
            <button
              className={`nav-button ${
                activeTab === 'overview' ? 'active' : ''
              }`}
              onClick={navigateToOverview}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">Overview</span>
            </button>
            <button
              className={`nav-button ${
                activeTab === 'create-course' ? 'active' : ''
              }`}
              onClick={navigateToCreateCourse}
            >
              <span className="nav-icon">➕</span>
              <span className="nav-label">Create {typeLabel}</span>
            </button>
            <button
              className={`nav-button ${
                activeTab === 'manage-categories' ? 'active' : ''
              }`}
              onClick={navigateToManageCategories}
            >
              <span className="nav-icon">📁</span>
              <span className="nav-label">Categories</span>
            </button>
          </nav>
        </div>

        {/* Course Selection Bar (only when editing) */}
        {selectedCourse &&
          (activeTab === 'structure' || activeTab === 'details') && (
            <div className="course-selection-bar">
              <div className="course-selector">
                <label>Currently Editing:</label>
                <select
                  value={selectedCourse?.slug || selectedCourse?.id || ''}
                  onChange={(e) => {
                    const key = e.target.value;
                    const course = courses.find(
                      (c) => String(c.slug || c.id) === key
                    );
                    if (course) handleCourseSelect(course);
                  }}
                  className="course-dropdown"
                >
                  <option value="">Select another {typeLabelLower}...</option>
                  {visibleCourses.map((course) => (
                    <option
                      key={course.id}
                      value={course.slug || course.id}
                    >
                      {course.title}{' '}
                      {course.status === 'draft' ? '(Draft)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button className="back-to-overview" onClick={navigateToOverview}>
                ← Back to Overview
              </button>
            </div>
          )}

        {/* Main Content Area */}
        <div className="editor-main-content">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="overview-header">
                <h2>
                  {typeFilter === 'project'
                    ? 'Project Overview'
                    : 'Course Overview'}
                </h2>
                <p>
                  {typeFilter === 'project'
                    ? 'Manage your projects and track engagement'
                    : 'Manage your teaching materials and track course performance'}
                </p>
              </div>

              {/* Quick Stats */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📚</div>
                  <div className="stat-content">
                    <h3>{visibleCourses.length}</h3>
                    <p>Total {typeLabelPlural}</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <h3>
                      {
                        visibleCourses.filter((c) => c.status === 'published')
                          .length
                      }
                    </h3>
                    <p>Published</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📝</div>
                  <div className="stat-content">
                    <h3>
                      {visibleCourses.filter((c) => c.status === 'draft').length}
                    </h3>
                    <p>Drafts</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">⭐</div>
                  <div className="stat-content">
                    <h3>
                      {visibleCourses.filter((c) => c.is_featured).length}
                    </h3>
                    <p>Featured</p>
                  </div>
                </div>
              </div>

              {/* List */}
              <div className="courses-section">
                <div className="section-header">
                  <h3>Your {typeLabelPlural}</h3>
                  <button
                    className="quick-action-button"
                    onClick={navigateToCreateCourse}
                  >
                    + New {typeLabel}
                  </button>
                </div>

                {visibleCourses.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      {typeFilter === 'project' ? '🛠️' : '📚'}
                    </div>
                    <h4>No {typeLabelPlural.toLowerCase()} yet</h4>
                    <p>
                      Create your first {typeLabelLower} to get started
                    </p>
                    <button
                      className="primary-action"
                      onClick={navigateToCreateCourse}
                    >
                      Create Your First {typeLabel}
                    </button>
                  </div>
                ) : (
                  <div className="courses-grid">
                    {visibleCourses.map((course) => (
                      <div key={course.id} className="course-card">
                        <div className="buttons-divs">
                          <div className="course-card-actions">
                            <button
                              className="edit-course-btn"
                              onClick={() => handleCourseSelect(course)}
                            >
                              Edit
                            </button>
                            <button
                              className="delete-course-btn"
                              onClick={() => handleDeleteClick(course)}
                              disabled={isLoading}
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        <div className="course-card-header">
                          <h4
                            onClick={() => handleCourseSelect(course)}
                            style={{ cursor: 'pointer' }}
                          >
                            {course.title}
                          </h4>
                          <span className={`status-badge ${course.status}`}>
                            {course.status}
                          </span>
                        </div>
                        <p className="course-description">
                          {course.short_description ||
                            'No description available'}
                        </p>
                        <div className="course-meta">
                          <span className="level">{course.level}</span>
                          <span className="price">${course.price}</span>
                          {course.is_featured && (
                            <span className="featured">⭐ Featured</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CREATE TAB */}
          {activeTab === 'create-course' && (
            <div className="create-course-tab">
              <div className="tab-header">
                <h2>Create New {typeLabel}</h2>
                <p>
                  Fill in the {typeLabelLower} details to create a new learning
                  experience
                </p>
              </div>
              <CourseCreator
                onCourseCreated={handleCourseCreated}
                existingCourses={visibleCourses}
                defaultIsProject={typeFilter === 'project'}
              />
            </div>
          )}

          {/* MANAGE CATEGORIES TAB */}
          {activeTab === 'manage-categories' && (
            <div className="manage-categories-tab">
              <div className="tab-header">
                <h2>Manage Categories</h2>
                <p>
                  Organize your courses with categories for better navigation
                </p>
              </div>
              <CategoryManager />
            </div>
          )}

          {/* EDIT - STRUCTURE */}
          {selectedCourse && activeTab === 'structure' && (
            <div className="edit-course-tab">
              <div className="course-editor-header">
                <h2>{typeLabel} Structure</h2>
                <p>
                  Build and organize your {typeLabelLower} content with sections
                  and lessons
                </p>
              </div>
              <div className="editor-tabs">
                <button
                  className={`tab-button ${
                    activeTab === 'structure' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('structure')}
                >
                  📚 {typeLabel} Structure
                </button>
                <button
                  className={`tab-button ${
                    activeTab === 'details' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('details')}
                >
                  ⚙️ {typeLabel} Details
                </button>
              </div>
              <div className="editor-content">
                <CourseStructure
                  course={selectedCourse}
                  onUpdate={handleStructureUpdate}
                  onSave={handleStructureSave}
                />
              </div>
            </div>
          )}

          {/* EDIT - DETAILS */}
          {selectedCourse && activeTab === 'details' && (
            <div className="edit-course-tab">
              <div className="course-editor-header">
                <h2>{typeLabel} Details</h2>
                <p>Update {typeLabelLower} information, pricing, and settings</p>
              </div>
              <div className="editor-tabs">
                <button
                  className={`tab-button ${
                    activeTab === 'structure' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('structure')}
                >
                  📚 {typeLabel} Structure
                </button>
                <button
                  className={`tab-button ${
                    activeTab === 'details' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('details')}
                >
                  ⚙️ {typeLabel} Details
                </button>
              </div>
              <div className="editor-content">
                <CourseDetailsEditForm
                  course={selectedCourse}
                  onUpdate={handleCourseUpdate}
                  isLoading={isLoading}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CourseEditor;